// src/LoginPage.js
// JOBFLOW NOW — Login Page
// Handles subscriber login. On success: stores JWT + tenant_id in localStorage, redirects to app.

import { useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5002';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Segoe UI', sans-serif",
    padding: '20px',
  },
  card: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#FFD700',
    letterSpacing: '1px',
  },
  logoSub: {
    fontSize: '12px',
    color: '#2da8ff',
    marginTop: '4px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2da8ff',
    marginBottom: '24px',
    textAlign: 'center',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#aaa',
    marginBottom: '6px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#111',
    border: '1px solid #444',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '15px',
    marginBottom: '16px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'black',
    color: '#FFA500',
    border: '4px solid #E8761D',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '1px',
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#3a0000',
    border: '1px solid #ff4444',
    borderRadius: '8px',
    padding: '12px 14px',
    color: '#ff6666',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  forgotLink: {
    display: 'block',
    textAlign: 'center',
    marginTop: '20px',
    color: '#2da8ff',
    fontSize: '13px',
    cursor: 'pointer',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    letterSpacing: '0.5px',
  },
};

export default function LoginPage({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pendingLoginSoundRef = useRef(null);
  const loginSoundHandedOffRef = useRef(false);

  const stopPendingLoginSound = () => {
    const sound = pendingLoginSoundRef.current;
    if (!sound) return;
    sound.pause();
    sound.currentTime = 0;
    pendingLoginSoundRef.current = null;
  };

  const prepareLoginSound = () => {
    try {
      if (localStorage.getItem('mytoolnow_sound_enabled') !== 'true') return null;

      const sound = new Audio('/SOUND.mp3');
      sound.muted = true;
      pendingLoginSoundRef.current = sound;
      void sound.play().catch((playError) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[MyTool NOW sound] Login audio preparation was blocked or failed.', playError);
        }
      });
      return sound;
    } catch (playError) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[MyTool NOW sound] Login audio could not be prepared.', playError);
      }
      return null;
    }
  };

  useEffect(() => () => {
    if (!loginSoundHandedOffRef.current) stopPendingLoginSound();
  }, []);

  const handleLogin = async () => {
    setError('');
    const trimEmail = email.trim().toLowerCase();
    const trimPassword = password.trim();

    if (!trimEmail || !trimPassword) {
      setError('Email and password are required.');
      return;
    }

    setLoading(true);
    const preparedSound = prepareLoginSound();
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimEmail, password: trimPassword, tenant_id: localStorage.getItem('tenant_id') || '' }),
      });

      const data = await res.json();

      if (!res.ok) {
        stopPendingLoginSound();
        setError(data.error || 'Login failed. Please try again.');
        return;
      }

      // Store JWT and tenant context in localStorage
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('tenant_id', String(data.user.tenant_id));
      localStorage.setItem('tenantId', String(data.user.tenant_id));
      localStorage.setItem('user_email', data.user.email);
      localStorage.setItem('user_role', data.user.role);
      localStorage.setItem('user_id', String(data.user.id));

      // Notify parent app
      loginSoundHandedOffRef.current = true;
      pendingLoginSoundRef.current = null;
      if (onLoginSuccess) onLoginSuccess(data.user, preparedSound);

    } catch (e) {
      stopPendingLoginSound();
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jfn-login-btn:hover { background-color: #FFC107 !important; color: #000 !important; }
      `}</style>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img
            src="/mtnlogo.png"
            alt="MyTool NOW"
            style={{ width: '220px', height: 'auto', display: 'block', margin: '0 auto', filter: 'drop-shadow(0 0 14px rgba(232,118,29,0.6))' }}
          />
          <div style={styles.logoSub}>SERVICE BUSINESS MANAGEMENT SOFTWARE</div>
        </div>

        <div style={styles.title}>Sign In</div>

        {error && <div style={styles.error}>{error}</div>}

        <label style={styles.label}>Email</label>
        <input
          style={styles.input}
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="you@example.com"
          autoComplete="email"
          disabled={loading}
        />

        <label style={styles.label}>Password</label>
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <input
            style={{ ...styles.input, marginBottom: 0, paddingRight: '44px' }}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="••••••••"
            autoComplete="current-password"
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            style={{
              position: 'absolute', right: '12px', top: '50%',
              transform: 'translateY(-50%)', background: 'none',
              border: 'none', cursor: 'pointer', color: '#888',
              fontSize: '18px', padding: '0', lineHeight: 1
            }}
            tabIndex={-1}
          >{showPassword ? '🙈' : '👁️'}</button>
        </div>

        <button
          className="jfn-login-btn"
          style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? '⏳ SIGNING IN...' : 'SIGN IN'}
        </button>

        <button
          style={styles.forgotLink}
          onClick={() => window.location.href = '/forgot-password'}
        >
          Forgot your password?
        </button>
      </div>
    </div>
  );
}
